/**
 * Lugares (§13.4.4, §14.2): nomes, artigos e regiões das 27 UFs e do Exterior (ZZ) como CONSTANTES de código, para que
 * herói, regiões e eventos não esperem `geography.json` (o `PlaceInfo.article` da geografia é gerado desta tabela).
 * Decisão editorial: Mato Grosso e Mato Grosso do Sul sem artigo (norma culta). Municípios, países e cidades do
 * exterior NÃO recebem preposição inferida (os modelos de texto evitam a construção).
 *
 * Folha (FE §4.2): não importa `contracts/`. Os códigos são os de `UfCode`/`PlaceCode`/`RegionCode` de
 * src/contracts/base.ts (o teste de tipo garante que as uniões são iguais).
 */
export { joinList } from './agree';

type Article = 'o' | 'a' | null;

/** Código de região (= `RegionCode` de contracts). */
export type RegionKey = 'N' | 'NE' | 'CO' | 'SE' | 'S' | 'EX';

interface PlaceEntry {
  name: string;
  article: Article;
  region: RegionKey;
}

/** 27 UFs + ZZ (ordem de `PLACE_CODES`): nome, artigo e região (§13.4.4). */
export const PLACES = {
  AC: { name: 'Acre', article: 'o', region: 'N' },
  AL: { name: 'Alagoas', article: null, region: 'NE' },
  AM: { name: 'Amazonas', article: 'o', region: 'N' },
  AP: { name: 'Amapá', article: 'o', region: 'N' },
  BA: { name: 'Bahia', article: 'a', region: 'NE' },
  CE: { name: 'Ceará', article: 'o', region: 'NE' },
  DF: { name: 'Distrito Federal', article: 'o', region: 'CO' },
  ES: { name: 'Espírito Santo', article: 'o', region: 'SE' },
  GO: { name: 'Goiás', article: null, region: 'CO' },
  MA: { name: 'Maranhão', article: 'o', region: 'NE' },
  MG: { name: 'Minas Gerais', article: null, region: 'SE' },
  MS: { name: 'Mato Grosso do Sul', article: null, region: 'CO' },
  MT: { name: 'Mato Grosso', article: null, region: 'CO' },
  PA: { name: 'Pará', article: 'o', region: 'N' },
  PB: { name: 'Paraíba', article: 'a', region: 'NE' },
  PE: { name: 'Pernambuco', article: null, region: 'NE' },
  PI: { name: 'Piauí', article: 'o', region: 'NE' },
  PR: { name: 'Paraná', article: 'o', region: 'S' },
  RJ: { name: 'Rio de Janeiro', article: 'o', region: 'SE' },
  RN: { name: 'Rio Grande do Norte', article: 'o', region: 'NE' },
  RO: { name: 'Rondônia', article: null, region: 'N' },
  RR: { name: 'Roraima', article: null, region: 'N' },
  RS: { name: 'Rio Grande do Sul', article: 'o', region: 'S' },
  SC: { name: 'Santa Catarina', article: null, region: 'S' },
  SE: { name: 'Sergipe', article: null, region: 'NE' },
  SP: { name: 'São Paulo', article: null, region: 'SE' },
  TO: { name: 'Tocantins', article: 'o', region: 'N' },
  ZZ: { name: 'Exterior', article: 'o', region: 'EX' },
} as const satisfies Record<string, PlaceEntry>;

/** Código de lugar (= `PlaceCode` de contracts: 27 UFs + ZZ). */
export type PlaceKey = keyof typeof PLACES;
/** Código de UF (= `UfCode` de contracts). */
export type UfKey = Exclude<PlaceKey, 'ZZ'>;

/** Regiões (§13.4.4) na ordem de `REGION_CODES`, com as UFs de cada uma. */
export const REGIONS = {
  N: { name: 'Norte', places: ['AC', 'AM', 'AP', 'PA', 'RO', 'RR', 'TO'] },
  NE: { name: 'Nordeste', places: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'] },
  CO: { name: 'Centro-Oeste', places: ['DF', 'GO', 'MS', 'MT'] },
  SE: { name: 'Sudeste', places: ['ES', 'MG', 'RJ', 'SP'] },
  S: { name: 'Sul', places: ['PR', 'RS', 'SC'] },
  EX: { name: 'Exterior', places: ['ZZ'] },
} as const satisfies Record<RegionKey, { name: string; places: readonly PlaceKey[] }>;

const entry = (code: string): PlaceEntry | undefined =>
  Object.hasOwn(PLACES, code) ? PLACES[code as PlaceKey] : undefined;

/** "Brasil" · "São Paulo" · "Exterior" (título). Código desconhecido devolve o próprio código. */
export function placeName(code: 'BR' | PlaceKey): string {
  if (code === 'BR') return 'Brasil';
  return entry(code)?.name ?? code;
}

/** Artigo do lugar ('o' | 'a' | null), a fonte de `PlaceInfo.article` (§14.2). */
export function placeArticle(code: PlaceKey): Article {
  return entry(code)?.article ?? null;
}

/** Região do lugar ("SP" → "SE"; "ZZ" → "EX"). */
export function regionOf(code: PlaceKey): RegionKey {
  return entry(code)?.region ?? 'EX';
}

/** "no Acre" · "na Bahia" · "em São Paulo" · "no exterior" (minúsculo no meio da frase). */
export function ufIn(code: PlaceKey): string {
  if (code === 'ZZ') return 'no exterior';
  const e = entry(code);
  if (!e) return code;
  return `${e.article === 'o' ? 'no' : e.article === 'a' ? 'na' : 'em'} ${e.name}`;
}

/** "do Acre" · "da Bahia" · "de São Paulo" · "do exterior". */
export function ufOf(code: PlaceKey): string {
  if (code === 'ZZ') return 'do exterior';
  const e = entry(code);
  if (!e) return code;
  return `${e.article === 'o' ? 'do' : e.article === 'a' ? 'da' : 'de'} ${e.name}`;
}

/** "Norte" · "Nordeste" · "Centro-Oeste" · "Sudeste" · "Sul" · "Exterior". */
export function regionName(code: RegionKey): string {
  return Object.hasOwn(REGIONS, code) ? REGIONS[code].name : code;
}

/** "no Norte" · "no Nordeste" · "no Centro-Oeste" · "no Sudeste" · "no Sul" · "no exterior". */
export function regionIn(code: RegionKey): string {
  if (code === 'EX') return 'no exterior';
  return Object.hasOwn(REGIONS, code) ? `no ${REGIONS[code].name}` : code;
}

let collator: Intl.Collator | null = null;

/** Ordem alfabética pelo nome (navegação ‹ ›, §8.2): `localeCompare(…, 'pt-BR')` sobre `placeName`. */
export function comparePlaceNames(a: 'BR' | PlaceKey, b: 'BR' | PlaceKey): number {
  collator ??= new Intl.Collator('pt-BR');
  return collator.compare(placeName(a), placeName(b));
}

/** As 27 UFs em ordem alfabética do nome (Acre, Alagoas, Amapá, Amazonas, Bahia…). */
export const UFS_BY_NAME: readonly UfKey[] = Object.freeze(
  (Object.keys(PLACES) as PlaceKey[]).filter((c): c is UfKey => c !== 'ZZ').sort(comparePlaceNames),
);
